/**
 * Reconciliation Panel
 * ====================
 * Main reconciliation view for drilling down into P&L cell transactions.
 *
 * Layout:
 * - Left sidebar: Expected amount, variance, status, anomalies, progress
 * - Main area: Transaction table with search/filters
 * - Modals: Transaction detail, PDF viewer, Sign-off
 * 
 * P3 Features:
 * - Real-time collaboration with presence indicators
 * - Mobile responsive layout
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import TransactionTable from './TransactionTable';
import TransactionDetailModal from './TransactionDetailModal';
import SignOffModal from './SignOffModal';
import PDFViewerModal from './PDFViewerModal';
import PatternStatsDashboard from './PatternStatsDashboard';
import { AlertTriangle, CheckCircle, Clock, Users, BarChart3, PenLine } from 'lucide-react';
import { useCollaboration, PresenceIndicator } from '../../hooks/useCollaboration';
import { FinancialValue } from '../shared/FinancialValue';
import { formatCurrency } from '../../utils/financialFormat';

interface ReconciliationPanelProps {
  reportId: string;
  year: number;
  month: number;
  section: string;
  lineItem: string;
  onBack: () => void;
  userId?: string;
  userName?: string;
  eventId?: string;
}

interface Transaction {
  id: string;
  date: string;
  vendor: string;
  description: string;
  amount: number;
  source_file: string;
  source_page: number;
  category: string;
  subcategory?: string;
  original_category?: string;
  is_reclassified: boolean;
  reclassification_reason?: string;
  checked: boolean;
  flagged: boolean;
  flag_reason?: string;
  needs_review: boolean;
}

interface CellData {
  cell_id: string;
  section: string;
  line_item: string;
  year: number;
  month: number;
  expected_amount: number;
  transactions: Transaction[];
  summary: {
    total_transactions: number;
    total_amount: number;
    variance: number;
    variance_pct: number;
    reviewed_count: number;
    flagged_count: number;
    reclassified_count: number;
  };
  anomalies: any[];
  reconciliation_status?: string;
}

export default function ReconciliationPanel({
  reportId,
  year,
  month,
  section,
  lineItem,
  onBack,
  userId = 'anonymous',
  userName = 'Anonymous User',
  eventId
}: ReconciliationPanelProps) {
  const [cellData, setCellData] = useState<CellData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showSignOffModal, setShowSignOffModal] = useState(false);
  const [showStatsDashboard, setShowStatsDashboard] = useState(false);
  
  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerFile, setPdfViewerFile] = useState('');
  const [pdfViewerPage, setPdfViewerPage] = useState(1);
  const [pdfViewerContext, setPdfViewerContext] = useState<{date: string; vendor: string; amount: number} | undefined>(undefined);

  // Real-time collaboration (P3 Feature)
  const handleTransactionUpdateFromWS = useCallback((update: any) => {
    // Refresh data when another user makes changes
    if (update.userId !== userId) {
      console.log('📡 Received update from:', update.userName, update.type);
      fetchCellData();
    }
  }, [userId]);

  const { isConnected, activeUsers, sendUpdate } = useCollaboration({
    reportId,
    userId,
    userName,
    onTransactionUpdate: handleTransactionUpdateFromWS,
    enabled: true
  });

  useEffect(() => {
    fetchCellData();
  }, [reportId, year, month, section, lineItem]);

  const fetchCellData = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        year: year.toString(),
        month: month.toString(),
        section: section,
        line_item: lineItem
      });

      // api.get returns data directly, not wrapped in response object
      const data = await api.get(`/api/v1/reconciliation/cell-transactions/${reportId}?${params.toString()}`);
      console.log('Cell transactions response:', data);
      setCellData(data);
    } catch (err: any) {
      console.error('Error fetching cell transactions:', err);
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
  };

  const handleTransactionUpdate = () => {
    // Refresh data after transaction is updated
    fetchCellData();
  };

  const handleSignOff = () => {
    setShowSignOffModal(true);
  };

  const handleSignOffComplete = () => {
    setShowSignOffModal(false);
    fetchCellData(); // Refresh to show new status
  };

  // Handle viewing source document
  const handleViewSourceDocument = (sourceFile: string, sourcePage: number, transaction: Transaction) => {
    setPdfViewerFile(sourceFile);
    setPdfViewerPage(sourcePage);
    setPdfViewerContext({
      date: transaction.date,
      vendor: transaction.vendor,
      amount: transaction.amount
    });
    setPdfViewerOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--color-brand)' }}></div>
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading transactions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border rounded-lg p-6" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Error</h3>
        <p style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 rounded"
          style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
        >
          Back to Grid
        </button>
      </div>
    );
  }

  if (!cellData) {
    return null;
  }

  const { summary, transactions, expected_amount, anomalies, reconciliation_status } = cellData;
  const varianceColor = Math.abs(summary.variance) < 1 ? 'var(--color-text-muted)' : summary.variance > 0 ? 'var(--color-needs-human-text)' : 'var(--color-verified)';
  const progressPercent = Math.round((summary.reviewed_count / summary.total_transactions) * 100);

  return (
    <div className="space-y-4">
      {/* Real-time Collaboration Header (P3 Feature) */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 rounded-lg shadow px-4 py-3" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="font-medium text-sm"
            style={{ color: 'var(--color-needs-human-text)' }}
          >
            ← Back to Grid
          </button>
          <span style={{ color: 'var(--color-text-ghost)' }}>|</span>
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {section} &gt; <span className="font-medium">{lineItem}</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Connection Status */}
          <div className="flex items-center gap-1.5 text-xs" style={{ color: isConnected ? 'var(--color-verified)' : 'var(--color-text-dim)' }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isConnected ? 'var(--color-verified)' : 'var(--color-text-dim)' }}></span>
            {isConnected ? 'Live' : 'Offline'}
          </div>
          {/* Presence Indicator */}
          <PresenceIndicator activeUsers={activeUsers} currentUserId={userId} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
      {/* Left Sidebar */}
      <div className="lg:col-span-1 space-y-4">
        {/* Expected Amount */}
        <div className="rounded-lg shadow p-4 lg:p-5" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-muted)' }}>Expected P&L Amount</div>
          <div className="text-2xl lg:text-3xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={expected_amount} /></div>
          <div className="text-sm truncate" style={{ color: 'var(--color-text-secondary)' }} title={lineItem}>{lineItem}</div>
        </div>

        {/* Transaction Summary */}
        <div className="rounded-lg shadow p-4 lg:p-5" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <div className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--color-text-muted)' }}>Transaction Summary</div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Count:</span>
              <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{summary.total_transactions}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Sum:</span>
              <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={summary.total_amount} /></span>
            </div>
            <div className="border-t pt-3" style={{ borderColor: 'var(--color-border-sub)' }}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold uppercase" style={{ color: 'var(--color-text-muted)' }}>Variance:</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xl lg:text-2xl font-bold" style={{ color: varianceColor }}>{formatCurrency(summary.variance)}</span>
                <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>({summary.variance_pct.toFixed(1)}%)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Verification Status */}
        <div className="rounded-lg shadow p-5" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <div className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--color-text-muted)' }}>Verification Status</div>
          <div className="space-y-2 mb-4">
            {reconciliation_status === 'reconciled' ? (
              <div className="flex items-center gap-2 text-sm px-3 py-2 rounded" style={{ color: 'var(--color-verified)', backgroundColor: 'transparent' }}>
                <CheckCircle size={16} />
                <span className="font-medium">Reconciled</span>
              </div>
            ) : reconciliation_status === 'in_review' ? (
              <div className="flex items-center gap-2 text-sm px-3 py-2 rounded" style={{ color: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                <Clock size={16} />
                <span className="font-medium">In Review</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm px-3 py-2 rounded" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' }}>
                <Clock size={16} />
                <span className="font-medium">Not Started</span>
              </div>
            )}
          </div>
          <button
            onClick={handleSignOff}
            className="w-full px-4 py-2 font-semibold rounded-lg transition-colors inline-flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            <PenLine size={16} /> Sign Off
          </button>
        </div>

        {/* Anomalies */}
        {(summary.flagged_count > 0 || summary.reclassified_count > 0 || anomalies.length > 0) && (
          <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
            <div className="flex items-center gap-2 text-sm font-bold mb-2" style={{ color: 'var(--color-needs-human-text)' }}>
              <AlertTriangle size={16} />
              Anomalies Detected
            </div>
            <ul className="space-y-1 text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
              {summary.flagged_count > 0 && (
                <li>• {summary.flagged_count} flagged transaction{summary.flagged_count > 1 ? 's' : ''}</li>
              )}
              {summary.reclassified_count > 0 && (
                <li>• {summary.reclassified_count} reclassified transaction{summary.reclassified_count > 1 ? 's' : ''}</li>
              )}
              {anomalies.map((anomaly, idx) => (
                <li key={idx}>• {anomaly.reason || 'Anomaly detected'}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Review Progress */}
        <div className="rounded-lg shadow p-5" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <div className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--color-text-muted)' }}>Review Progress</div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span style={{ color: 'var(--color-text-secondary)' }}>Reviewed:</span>
              <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>{summary.reviewed_count}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: 'var(--color-text-secondary)' }}>Flagged:</span>
              <span className="font-semibold" style={{ color: 'var(--color-brand)' }}>{summary.flagged_count}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: 'var(--color-text-secondary)' }}>Total:</span>
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{summary.total_transactions}</span>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full rounded-full h-2.5" style={{ backgroundColor: 'var(--color-bg-hover)' }}>
              <div
                className="h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%`, backgroundColor: 'var(--color-verified)' }}
              ></div>
            </div>
            <div className="text-center text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{progressPercent}% complete</div>
          </div>
        </div>

        {/* Pattern Analytics Button */}
        {eventId && (
        <div className="rounded-lg shadow p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <button
            onClick={() => setShowStatsDashboard(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 font-medium rounded-lg transition-colors"
            style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
            data-testid="view-pattern-stats-btn"
          >
            <BarChart3 size={18} />
            Pattern Analytics
          </button>
        </div>
        )}
      </div>

      {/* Main Content - Transaction Table */}
      <div className="lg:col-span-3">
        <TransactionTable
          transactions={transactions}
          expectedAmount={expected_amount}
          reportId={reportId}
          eventId={eventId}
          onTransactionClick={handleTransactionClick}
          onTransactionsUpdate={handleTransactionUpdate}
          onViewSourceDocument={handleViewSourceDocument}
        />
      </div>
      </div>

      {/* Modals */}
      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          reportId={reportId}
          onClose={() => setSelectedTransaction(null)}
          onUpdate={handleTransactionUpdate}
        />
      )}

      {showSignOffModal && (
        <SignOffModal
          reportId={reportId}
          cellId={cellData.cell_id}
          expectedAmount={expected_amount}
          actualAmount={summary.total_amount}
          variance={summary.variance}
          reclassifiedCount={summary.reclassified_count}
          onClose={() => setShowSignOffModal(false)}
          onComplete={handleSignOffComplete}
        />
      )}

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerOpen}
        reportId={reportId}
        sourceFile={pdfViewerFile}
        sourcePage={pdfViewerPage}
        transactionContext={pdfViewerContext}
        onClose={() => setPdfViewerOpen(false)}
      />

      {/* Pattern Statistics Dashboard Modal */}
      {showStatsDashboard && eventId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <PatternStatsDashboard
              eventId={eventId}
              onClose={() => setShowStatsDashboard(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
